import React, { useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-js';

import { useDarkMode } from './useDarkMode';
import { lightTheme, darkTheme } from './resources/styles/themes/theme';
import GlobalStyles from './resources/styles/global';
import Header from './components/Header';
import Content from './components/Content';
import Footer from './components/Footer';

const App = () => {
  const useStyles = makeStyles({
    root: {
      height: '10px',
      '&.MuiLinearProgress-colorPrimary:not(.MuiLinearProgress-buffer)': {
        backgroundColor: '#D7CAAA',
      },
      '& .MuiLinearProgress-colorPrimary': {
        backgroundColor: '#D7CAAA',
      },
      '& .MuiLinearProgress-barColorPrimary': {
        backgroundColor: '#363537',
      },
      '& .MuiLinearProgress-dashedColorPrimary': {
        backgroundImage:
          'radial-gradient(#D7CAAA 0%, #D7CAAA 16%, transparent 42%)',
      },
    },
  });

  const classes = useStyles();
  const [sharedData, setSharedData] = useState();
  const [resumeData, setResumeData] = useState();
  const [loading, setLoading] = React.useState(true);

  const swapCurrentlyActiveLanguage = (LangIconId) => {
    let chosenLangIconId = LangIconId;
    if (LangIconId === window.$primaryLanguageIconId) {
      chosenLangIconId = window.$primaryLanguageIconId;
    }
    if (LangIconId === window.$secondaryLanguageIconId) {
      chosenLangIconId = window.$secondaryLanguageIconId;
    }
    if (LangIconId === window.$thirdLanguageIconId) {
      chosenLangIconId = window.$thirdLanguageIconId;
    }

    let flag = document.getElementById(window.$primaryLanguageIconId);
    if (flag) {
      flag.setAttribute('filter', 'brightness(40%)');
    }
    flag = document.getElementById(window.$secondaryLanguageIconId);
    if (flag) {
      flag.setAttribute('filter', 'brightness(40%)');
    }
    flag = document.getElementById(window.$thirdLanguageIconId);
    if (flag) {
      flag.setAttribute('filter', 'brightness(40%)');
    }

    flag = document.getElementById(chosenLangIconId);
    if (flag) {
      flag.removeAttribute('filter', 'brightness(40%)');
    }
  };

  const API_URL = 'https://yhqo2vwdifggdj3bcw2uczbloe.appsync-api.ca-central-1.amazonaws.com/graphql';
  const AWS_ACCESS_KEY_ID = 'AKIA6AGSDW6IS2RZIQZK';
  const AWS_SECRET_ACCESS_KEY = '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';

  const apiUrl = new URL(API_URL);

  const signer = new SignatureV4({
    service: 'appsync',
    region: 'ca-central-1',
    credentials: {
      accessKeyId: AWS_ACCESS_KEY_ID,
      secretAccessKey: AWS_SECRET_ACCESS_KEY,
    },
    sha256: Sha256,
  });

  const MyGraphqlQuery = (path) => {
    const query = {
      query: `
        query MyQuery($id: ID!) {
          getJtdevInfo(id: $id) {
              id
              content
          }
        }
      `,
      variables: { id: path },
    };
    return query;
  };

  const loadResumeFromPath = async (path) => {
    // set up the HTTP request
    const request = await new HttpRequest({
      hostname: apiUrl.host,
      path: apiUrl.pathname,
      body: JSON.stringify(MyGraphqlQuery(path)),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        host: apiUrl.hostname,
      },
    });

    const signedRequest = await signer.sign(request);

    const { headers, body, method } = await signedRequest;

    await fetch(API_URL, {
      headers,
      body,
      method,
    })
      .then((res) => res.json())
      .then((res) => {
        setResumeData(JSON.parse(res.data.getJtdevInfo.content));
        setLoading(false);
      });
  };

  const loadPickedLanguage = async (pickedLanguage, selectedIconId) => {
    await swapCurrentlyActiveLanguage(selectedIconId);
    document.documentElement.lang = pickedLanguage;
    let resumePath = '';
    if (document.documentElement.lang === window.$primaryLanguage) {
      resumePath = 'res_en';
    }
    if (document.documentElement.lang === window.$secondaryLanguage) {
      resumePath = 'res_br';
    }
    if (document.documentElement.lang === window.$thirdLanguage) {
      resumePath = 'res_fr';
    }
    await loadResumeFromPath(resumePath);
  };

  const [theme, toggleTheme, componentMounted] = useDarkMode();

  const globalTheme = theme === 'light' ? lightTheme : darkTheme;

  const loadSharedData = async (path) => {
    // set up the HTTP request
    const request = await new HttpRequest({
      hostname: apiUrl.host,
      path: apiUrl.pathname,
      body: JSON.stringify(MyGraphqlQuery(path)),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        host: apiUrl.hostname,
      },
    });

    const signedRequest = await signer.sign(request);

    const { headers, body, method } = await signedRequest;

    await fetch(API_URL, {
      headers,
      body,
      method,
    })
      .then((res) => res.json())
      .then((res) => {
        setSharedData(JSON.parse(res.data.getJtdevInfo.content));
      });
  };

  const loadAppData = async () => {
    setLoading(true);
    await loadSharedData('portfolio_shared_data');
    await loadPickedLanguage(window.$primaryLanguage, window.$primaryLanguageIconId);
  };

  useEffect(() => {
    loadAppData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !componentMounted) {
    return <LinearProgress className={classes.root} />;
  }
  return (
    <>
      {loading && <LinearProgress className={classes.root} />}
      {!loading
        && (
        <ThemeProvider theme={globalTheme}>
          <GlobalStyles />
          <Grid container alignItems="center" justifyContent="center">
            <Grid item xs={12} md={8}>
              <Header
                theme={theme}
                toggleTheme={toggleTheme}
                loadPickedLanguage={loadPickedLanguage}
                navbarItems={resumeData.basic_info.navbarItems}
              />
            </Grid>
            <Grid item xs={12}>
              <Content
                sharedData={sharedData}
                resumeData={resumeData}
              />
              <Footer
                sharedBasicInfo={sharedData.basic_info}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
        )}
    </>
  );
};

export default App;
